import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { NAV_ITEMS } from '@/router/navigation';
import type { NavBadgeKey, NavTone } from '@/router/navigation';
import { cn } from '@/lib/cn';
import { useSessionStore } from '@/store/useSessionStore';
import { useMerchantStore } from '@/store/useMerchantStore';
import { useNavBadgesStore } from '@/store/useNavBadgesStore';
import { FastrrLogo } from '@/components/icons';

interface SidebarProps {
  isOpen: boolean;
  onNavigate: () => void;
}

/** Where the "Need help?" footer links to. Adjust to your support destination. */
const SUPPORT_URL = 'https://support.shiprocket.in/';

/** Soft-colour chip classes, shared by the icon chip and the count badge. */
const TONE_CLASSES: Record<NavTone, string> = {
  brand: 'bg-brand-soft text-brand',
  neutral: 'bg-surface-sunken text-ink-muted',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  info: 'bg-info-soft text-info',
};

/** Per-item nav icons (20×20, inherit `currentColor` from the chip). */
const NAV_ICONS: Record<string, ReactNode> = {
  dashboard: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="10" cy="10" r="7" />
      <path d="M10 3v7h7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  returns: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M7 4 3 8l4 4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 8h8a5 5 0 0 1 5 5v1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  exchanges: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3 7h11l-3-3M17 13H6l3 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  refunds: <span className="text-[15px] font-semibold leading-none">₹</span>,
  qc: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M10 2.5 4 5v4.5c0 3.4 2.5 5.9 6 7.5 3.5-1.6 6-4.1 6-7.5V5l-6-2.5Z" strokeLinejoin="round" />
      <path d="m7.4 9.6 2 2 3.4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  exceptions: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M10 3 2.5 16.5h15L10 3Z" strokeLinejoin="round" />
      <path d="M10 8v4M10 14.4h.01" strokeLinecap="round" />
    </svg>
  ),
  analytics: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 16V9M10 16V4M16 16v-5" strokeLinecap="round" />
    </svg>
  ),
  blocklist: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="10" cy="10" r="7" />
      <path d="m5 5 10 10" strokeLinecap="round" />
    </svg>
  ),
  autopilot: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="4" y="7" width="12" height="8" rx="2" />
      <path d="M10 7V4.5M2.8 10.5v2M17.2 10.5v2" strokeLinecap="round" />
      <path d="M7.5 11h.01M12.5 11h.01" strokeLinecap="round" />
    </svg>
  ),
  settings: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="10" cy="10" r="2.6" />
      <path
        d="M10 2.5v2.2M10 15.3v2.2M2.5 10h2.2M15.3 10h2.2M4.7 4.7l1.6 1.6M13.7 13.7l1.6 1.6M15.3 4.7l-1.6 1.6M6.3 13.7l-1.6 1.6"
        strokeLinecap="round"
      />
    </svg>
  ),
};

function ChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="m6 4 4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// function ChevronDown() {
//   return (
//     <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
//       <path d="m4 6 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
//     </svg>
//   );
// }

function HeadsetIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 11v-1a6 6 0 0 1 12 0v1" strokeLinecap="round" />
      <rect x="2.5" y="10.5" width="3.2" height="5" rx="1.2" />
      <rect x="14.3" y="10.5" width="3.2" height="5" rx="1.2" />
      <path d="M16 15.5v.5a2.5 2.5 0 0 1-2.5 2.5H10.5" strokeLinecap="round" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M11 3h4v4M15 3 8.5 9.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 10.5V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Sidebar({ isOpen, onNavigate }: SidebarProps) {
  const merchantDomain = useSessionStore((state) => state.merchantDomain);
  const merchantName = useMerchantStore((state) => state.merchantName);
  const qcQueue = useNavBadgesStore((state) => state.qcQueue);
  const exceptions = useNavBadgesStore((state) => state.exceptions);
  const loadBadges = useNavBadgesStore((state) => state.loadBadges);

  useEffect(() => {
    loadBadges();
  }, [loadBadges]);

  const badgeCounts: Record<NavBadgeKey, number> = { qcQueue, exceptions };
  const displayName = merchantName || merchantDomain || 'Store';
  const showDomain = Boolean(merchantDomain) && merchantDomain !== displayName;

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={onNavigate}
        />
      ) : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-line bg-surface transition-transform lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Store header */}
        <div className="flex items-center gap-3 px-4 py-4">
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center text-brand">
            <FastrrLogo />
          </span>
          <div className="min-w-0 flex-1">
            <span className="block truncate text-sm font-bold tracking-tight text-ink">{displayName}</span>
            {showDomain ? (
              <span className="block truncate text-xs font-normal text-ink-muted">{merchantDomain}</span>
            ) : null}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
          {NAV_ITEMS.map((item) => {
            const count = item.badgeKey ? badgeCounts[item.badgeKey] : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 rounded-xl px-2.5 py-2 text-sm transition-colors',
                    isActive ? 'bg-brand-soft' : 'hover:bg-surface-muted',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg',
                        TONE_CLASSES[item.tone],
                      )}
                    >
                      {NAV_ICONS[item.iconKey]}
                    </span>
                    <span
                      className={cn(
                        'flex-1 truncate font-medium',
                        isActive ? 'text-brand' : 'text-ink group-hover:text-ink',
                      )}
                    >
                      {item.label}
                    </span>
                    {count > 0 ? (
                      <span
                        className={cn(
                          'flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold',
                          TONE_CLASSES[item.tone],
                        )}
                      >
                        {count}
                      </span>
                    ) : null}
                    <span className={cn('flex-shrink-0', isActive ? 'text-brand' : 'text-ink-subtle')}>
                      <ChevronRight />
                    </span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Help footer */}
        <div className="px-3 pb-4 pt-2">
          <a
            href={SUPPORT_URL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 rounded-xl bg-brand-soft px-3 py-3 transition-colors hover:bg-brand-soft/70"
          >
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
              <HeadsetIcon />
            </span>
            <div className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-brand">Need help?</span>
              <span className="block truncate text-xs text-ink-muted">Contact support</span>
            </div>
            <span className="flex-shrink-0 text-brand">
              <ExternalLinkIcon />
            </span>
          </a>
        </div>
      </aside>
    </>
  );
}
